import React, { useState, useEffect, createContext, useContext } from 'react'
import Api from 'utils/api'
import * as XLSX from 'xlsx'

const initCardHoldersContextPropsState = {
  loading: true,
  users: [],
  setUsers: () => {},
  searchUser: () => {},
  downloadExcel: () => {},
  downloadCSV: () => {},
  filterUser: () => {},
  refreshList: () => {},
}

const CardHoldersContext = createContext(initCardHoldersContextPropsState)

const useCardHolders = () => {
  return useContext(CardHoldersContext)
}

const CardHoldersProvider = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilters, setActiveFilters] = useState([])

  const performFilter = (values, query) =>
    values.find((item) => item?.toLowerCase().includes(query.toLowerCase()))

  const searchUser = (e) => {
    const { value } = e.target
    setSearchQuery(value || '')
    if (!value || value.trim() === '') {
      // Apply role filters if any
      if (activeFilters.length) {
        const filtered = users.filter((item) => {
          return activeFilters.includes(item.role)
        })
        setFilteredUsers([...filtered])
      } else {
        setFilteredUsers([...users])
      }
      return
    }
    const filtered = users.filter((user) =>
      performFilter([user.firstName, user.lastName, user.email], value)
    )
    // Apply role filters if any
    if (activeFilters.length) {
      const roleFiltered = filtered.filter((item) => {
        return activeFilters.includes(item.role)
      })
      setFilteredUsers([...roleFiltered])
    } else {
      setFilteredUsers([...filtered])
    }
  }

  const filterUser = (filters) => {
    setActiveFilters(filters)
    if (!filters.length) {
      // Apply search if any
      if (searchQuery && searchQuery.trim() !== '') {
        const filtered = users.filter((user) =>
          performFilter([user.firstName, user.lastName, user.email], searchQuery)
        )
        setFilteredUsers([...filtered])
      } else {
        setFilteredUsers([...users])
      }
      return
    }
    const filtered = users.filter((item) => {
      return filters.includes(item.role)
    })
    // Apply search if any
    if (searchQuery && searchQuery.trim() !== '') {
      const searchFiltered = filtered.filter((user) =>
        performFilter([user.firstName, user.lastName, user.email], searchQuery)
      )
      setFilteredUsers([...searchFiltered])
    } else {
      setFilteredUsers([...filtered])
    }
  }

  const getTeam = async () => {
    const { response } = await Api('/user', 'get')
    setLoading(false)
    if (response) {
      setUsers(response.data)
      setFilteredUsers(response.data)
    }
  }

  const getData = () => {
    const data = [['First Name', 'Last Name', 'Email', 'Phone', 'Role', 'Status']]

    const user_data = users.map((user) => [
      user.firstName || '',
      user.lastName || '',
      user.email || '',
      user.phone?.toString() || '',
      user.role || '',
      user.status || '',
    ])

    data.push(...user_data)
    return data
  }

  const downloadExcel = () => {
    const data = getData()
    const workbook = XLSX.utils.book_new()
    const sheet = XLSX.utils.aoa_to_sheet(data)
    XLSX.utils.book_append_sheet(workbook, sheet, 'Sheet1')
    return XLSX.writeFile(workbook, 'team-members.xlsx')
  }

  const downloadCSV = () => {
    const data = getData()

    const csvContent = data.map((row) => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'cardholders.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const onLoad = () => {
    getTeam()
  }

  useEffect(onLoad, [])

  // Custom setUsers that also updates filteredUsers by reapplying filters
  const updateUsers = (newUsers) => {
    setUsers(newUsers)
    // Reapply search and filters
    let filtered = newUsers
    if (searchQuery && searchQuery.trim() !== '') {
      filtered = filtered.filter((user) =>
        performFilter([user.firstName, user.lastName, user.email], searchQuery)
      )
    }
    if (activeFilters.length) {
      filtered = filtered.filter((item) => {
        return activeFilters.includes(item.role)
      })
    }
    setFilteredUsers([...filtered])
  }

  return (
    <CardHoldersContext.Provider
      value={{
        loading,
        users: filteredUsers.length > 0 ? filteredUsers : users,
        setUsers: updateUsers,
        searchUser,
        downloadExcel,
        downloadCSV,
        filterUser,
        refreshList: onLoad,
      }}
    >
      {children}
    </CardHoldersContext.Provider>
  )
}

export { CardHoldersProvider, useCardHolders }
